import { IBudgetStatusCounts, IBudgetKanbanFilters, IBudgetsListResponse, IBudgetVersionItem, IBudget, IBudgetServiceItemResponse, IBudgetServiceItem, IBudgetServiceOption, IBudgetServicesResponse, IBudgetVersionService, IBudgetRequest, IBudgetDecideRequest, IBudgetAttachment } from "../models/budgetV2";
import { BudgetServiceType } from "../components/budgetsV2/enums/budgetService";
import { dedupeServiceItems } from "../components/budgetsV2/utils/dedupeServiceItems";
import ApiService from "../services/api";
import { toLocalDateTimeString } from "../utils/dateUtils";
import { BudgetRecipient } from "../components/budgetsV2/enums/budgetRecipient";

const sumCounts = (data: any[], statuses: string[]): number =>
  data.filter((item) => statuses.includes(item.status)).reduce((acc, item) => acc + (item.count || 0), 0);

export const fetchBudgetV2StatusCounts = async (filters: IBudgetKanbanFilters): Promise<IBudgetStatusCounts> => {
  try {
    const apiService = new ApiService(undefined, "v2");
    const params = new URLSearchParams();

    if (filters.startUpdatedAt) params.append("startUpdatedAt", toLocalDateTimeString(filters.startUpdatedAt));
    if (filters.endUpdatedAt) params.append("endUpdatedAt", toLocalDateTimeString(filters.endUpdatedAt, true));
    if (filters.chassis) params.append("chassis", filters.chassis);
    if (filters.status) params.append("status", filters.status);

    const queryString = params.toString();
    const url = `/budgets/status-counts${queryString ? `?${queryString}` : ""}`;

    const response = await apiService.get<any>(url);

    if (response.status >= 200 && response.status < 300 && response.data) {
      const data = response.data;

      // O backend devolve o status real do orçamento (BudgetStatus): qualquer aprovação conta como "aprovado".
      return {
        sent: sumCounts(data, ["SENT"]),
        altered: sumCounts(data, ["UPDATED"]),
        counterproposal: sumCounts(data, ["COUNTER_PROPOSED"]),
        approved: sumCounts(data, ["APPROVED_BY_MANAGER", "APPROVED_BY_FACTORY", "APPROVED_BY_CONSULTANT", "APPROVED"]),
        rejected: sumCounts(data, ["REJECTED"]),
      };


    }

    throw new Error("Failed to fetch budget status counts: " + response.statusText);
  } catch (error) {
    return { sent: 0, altered: 0, counterproposal: 0, approved: 0, rejected: 0 };
  }
}

export const fetchBudgetsV2 = async (filters: IBudgetKanbanFilters): Promise<IBudgetsListResponse> => {
  try {
    const apiService = new ApiService(undefined, "v2");
    const params = new URLSearchParams();

    if (filters.startUpdatedAt) params.append("startUpdatedAt", toLocalDateTimeString(filters.startUpdatedAt));
    if (filters.endUpdatedAt) params.append("endUpdatedAt", toLocalDateTimeString(filters.endUpdatedAt, true));
    if (filters.chassis) params.append("chassis", filters.chassis);
    if (filters.status) params.append("status", filters.status);
    if (filters.column) params.append("column", filters.column);
    params.append("page", (filters.page ?? 1).toString());
    params.append("limit", (filters.limit ?? 20).toString());

    const queryString = params.toString();
    const url = `/budgets?${queryString}`;

    const response = await apiService.get<IBudgetsListResponse>(url);

    if (response.status >= 200 && response.status < 300 && response.data) {
      return response.data;
    }

    throw new Error("Failed to fetch budgets: " + response.statusText);
  } catch (error) {
    return { content: [], totalElements: 0, totalPages: 0, last: true, first: true };
  }
}

export const fetchBudgetsByOccurrenceV2 = async (
  occurrenceUuid: string,
  page: number = 1,
  limit: number = 50
): Promise<IBudgetsListResponse> => {
  try {
    const apiService = new ApiService(undefined, "v2");
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response = await apiService.get<IBudgetsListResponse>(`/budgets/occurrences/${occurrenceUuid}?${params.toString()}`);

    if (response.status >= 200 && response.status < 300 && response.data) {
      return response.data;
    }

    throw new Error("Failed to fetch budgets by occurrence: " + response.statusText);
  } catch (error) {
    return { content: [], totalElements: 0, totalPages: 0, last: true, first: true };
  }
}

export const fetchBudgetVersionsV2 = async (budgetId: string): Promise<IBudgetVersionItem[]> => {
  const apiService = new ApiService(undefined, "v2");
  try {
    const response = await apiService.get<IBudgetVersionItem[]>(`/budgets/${budgetId}/versions`);

    if (response.status === 200 && response.data) {
      return response.data;
    }
    throw new Error("Failed to fetch budget versions: " + response.statusText);
  } catch (error) {
    return [];
  }
};

export const fetchBudgetV2 = async (budgetId: string, budgetVersion: number): Promise<IBudget> => {
  const apiService = new ApiService(undefined, "v2");
  try {
    const response = await apiService.get<IBudget>(`/budgets/${budgetId}/versions/${budgetVersion}`);

    // Merge serviceItems and items into serviceItems to resolve old version data
    const resolveItemsOfServices = (services?: IBudgetVersionService[]): IBudgetVersionService[] => {
      return (services ?? []).map(service => ({...service, serviceItems: dedupeServiceItems([...(service.serviceItems ?? []), ...(service.items ?? [])])}));
    };

    console.log({ 
      ...response.data, 
      versionData: { 
        ...response.data?.versionData, 
        services: resolveItemsOfServices(response.data?.versionData?.services), 
        proposalServices: resolveItemsOfServices(response.data?.versionData?.proposalServices) 
      } 
    })

    return { 
      ...response.data, 
      versionData: { 
        ...response.data?.versionData, 
        services: resolveItemsOfServices(response.data?.versionData?.services), 
        proposalServices: resolveItemsOfServices(response.data?.versionData?.proposalServices) 
      } 
    };
  } catch (error) {
    throw new Error("Failed to fetch budget: " + error);
  }
};

export const exportBudgetsV2 = async (filters: IBudgetKanbanFilters): Promise<{ errorMessage: string | null, successMessage: string | null }> => {
  const apiService = new ApiService(undefined, "v2");
  const params = new URLSearchParams();

  if (filters.startUpdatedAt) params.append("startUpdatedAt", toLocalDateTimeString(filters.startUpdatedAt));
  if (filters.endUpdatedAt) params.append("endUpdatedAt", toLocalDateTimeString(filters.endUpdatedAt, true));
  if (filters.chassis) params.append("chassis", filters.chassis);
  if (filters.status) params.append("status", filters.status);

  const queryString = params.toString();
  const url = `/budgets/export?${queryString}`;
  try {
    const response = await apiService.get<void>(url);

    if (response.status >= 200 && response.status < 300 && response.data) {
      const contentDisposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];

      let filename = "orcamentos-aprovados.xlsx";
      if (typeof contentDisposition === "string") {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        const rawFilename = utf8Match?.[1] || plainMatch?.[1];
        if (rawFilename) {
          filename = decodeURIComponent(rawFilename);
        }
      }

      const blob = response.data as Blob
        ? response.data
        : new Blob([response.data as BlobPart]);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { errorMessage: null, successMessage: "Orçamentos baixados com sucesso!" };
    } else {
      return { errorMessage: "Erro ao baixar orçamentos", successMessage: null };
    }
  } catch (error) {
    throw new Error("Failed to export budgets: " + error);
  }
};

export const fetchBudgetServicesV2 = async (
  serviceType: BudgetServiceType,
  chassis: string,
  term: string,
  page: number = 1,
  limit: number = 20,
  odometer?: string,
  hourMeter?: string
): Promise<IBudgetServiceOption[]> => {
  try {
    const apiService = new ApiService(undefined, "v2");

    const params = new URLSearchParams();
    if (chassis) params.append("chassis", chassis);
    if (odometer) params.append("odometer", odometer);
    if (hourMeter) params.append("hourMeter", hourMeter);
    
    const trimmed = term.trim();
    if (trimmed) {
      params.append("searchTerm", trimmed);
    }
    params.append("page", String(page));
    params.append("limit", String(limit));

    const response = await apiService.get<IBudgetServicesResponse>(
      `/budgets/support/services/${serviceType}?${params.toString()}`
    );

    if (response.status < 400 && response.data?.items) {
      return response.data.items;
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const fetchBudgetServiceItemsV2 = async (
  {
    serviceType,
    serviceId,
    chassis,
    odometer,
    hourMeter,
    searchTerm,
    page,
    limit,
  }: {
    serviceType?: BudgetServiceType;
    serviceId?: string;
    chassis?: string;
    odometer?: string;
    hourMeter?: string;
    searchTerm?: string;
    page?: number;
    limit?: number;
  }
): Promise<IBudgetServiceItem[]> => {
  try {
    const apiService = new ApiService(undefined, "v2");
    const params = new URLSearchParams();
    
    if (chassis) params.append("chassis", chassis);
    if (odometer) params.append("odometer", odometer);
    if (hourMeter) params.append("hourMeter", hourMeter);
    if (searchTerm) params.append("searchTerm", searchTerm);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));

    const url = serviceType && serviceId ? `/budgets/support/services/${serviceType}/${encodeURIComponent(serviceId)}/items?${params.toString()}` : `/budgets/support/services/items?${params.toString()}`;

    const response = await apiService.get<IBudgetServiceItemResponse>(url);

    if (response.status >= 200 && response.status < 300 && response.data) {
      return response.data.items;
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const createBudgetV2 = async (payload: IBudgetRequest): Promise<string> => {
  const apiService = new ApiService(undefined, "v2");
  try {
    const response = await apiService.post<string>("/budgets", payload);

    if (response.status >= 200 && response.status < 300 && response.data) {
      return response.data;
    }

    throw new Error("Failed to create budget: " + response.statusText);
  } catch (error) {
    throw new Error("Failed to create budget: " + error);
  }
};

export const updateBudgetV2 = async (budgetId: string, payload: IBudgetRequest): Promise<string> => {
  const apiService = new ApiService(undefined, "v2");
  try {
    const response = await apiService.patch<string>(`/budgets/${budgetId}`, payload);

    if (response.status >= 200 && response.status < 300 && response.data) {
      return response.data;
    }

    throw new Error("Failed to update budget: " + response.statusText);
  } catch (error) {
    throw new Error("Failed to update budget: " + error);
  }
};

export const decideBudgetV2 = async (budgetId: string, payload: IBudgetDecideRequest): Promise<void> => {
  const apiService = new ApiService(undefined, "v2");
  try {
    const response = await apiService.patch<void>(`/budgets/${budgetId}/decide`, payload);

    if (response.status >= 200 && response.status < 300) {
      return;
    }

    throw new Error("Failed to decide budget: " + response.statusText);
  } catch (error) {
    throw new Error("Failed to decide budget: " + error);
  }
};

export const uploadBudgetFilesV2 = async (
  budgetId: string,
  files: File[]
): Promise<boolean> => {
  if (files.length === 0) {
    return true;
  }

  try {
    const apiService = new ApiService();

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      const url = `/attachment/budget/${budgetId}/upload`;

      const response = await apiService.post<void>(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status < 200 || response.status >= 300) {
        return false;
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const downloadBudgetAttachmentV2 = async (
  budgetId: string,
  filename: string
): Promise<any> => {
  try {
    const apiService = new ApiService();
    return await apiService.getAndDownloadFile(
      `/attachment/budget/${budgetId}/${filename}/download`
    );
  } catch (error) {
    console.error("[downloadBudgetAttachmentV2] erro ao baixar anexo", error);
    throw error;
  }
};

export const fetchBudgetAttachmentsV2 = async (
  budgetId: string
): Promise<IBudgetAttachment[]> => {
  try {
    const apiService = new ApiService();
    const url = `/attachment/budget/${budgetId}`;

    const response = await apiService.get<IBudgetAttachment[]>(url);

    if (response.status < 200 || response.status >= 300) {
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error("[fetchBudgetAttachmentsV2] erro ao buscar anexos", error);
    return [];
  }
};

export const fetchBudgetUserProfile = async (): Promise<BudgetRecipient | undefined> => {
  try {
    const apiService = new ApiService();
    const response = await apiService.get<any>("/users/role");

    if (response.status >= 200 && response.status < 300 && response.data) {
      const roleFromApi = response.data.role || response.data;

      if (!roleFromApi || typeof roleFromApi !== "string") {
        return undefined;
      }

      if (roleFromApi === "TOWER") {
        return BudgetRecipient.TOWER;
      }

      return roleFromApi as BudgetRecipient;
    }

    return undefined;
  } catch (error) {
    return undefined;
  }
};